/**
 * Cart validation for special collections
 * Adds CSS classes to body based on cart contents
 */

class CartValidation {
  constructor() {
    // Product types to look for
    this.customBarWrapperType = 'Customised';
    this.cakeType = 'Custom Cake';
    this.CustomBox = 'Bundle';
    this.init();
  }

  init() {
    // Run on page load
    this.checkCartConflicts();
    
    // Re-run when cart changes
    document.addEventListener('cart:update', () => this.checkCartConflicts());
  }

  /**
   * Get current cart items
   */
  async getCurrentCartItems() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      
      const cart = await response.json();
      return cart.items || [];
    } catch (error) {
      console.warn('[CartValidation] Error fetching cart:', error);
      return [];
    }
  }

  /**
   * Remove rush fee if it's the only item in cart
   */
  async removeRushFeeIfAlone() {
    try {
      const cartItems = await this.getCurrentCartItems();
      
      // Check if there's only 1 item and it's the rush fee (ID: 42025847226430)
      if (cartItems.length === 1 && cartItems[0].variant_id === 42025847226430) {
        console.log('[CartValidation] Removing rush fee - it\'s alone in cart');
        
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: cartItems[0].key,
            quantity: 0
          })
        });
        
        if (response.ok) {
          // Refresh cart/minicart
          location.reload();
        }
      }
    } catch (error) {
      console.warn('[CartValidation] Error removing rush fee:', error);
    }
  }

  /**
   * Check for cart conflicts and add do-not-add-cart class if needed
   */
  async checkCartConflicts() {
    console.log('[CartValidation] Checking cart conflicts...');
    
    // Remove rush fee if it's alone in cart
    await this.removeRushFeeIfAlone();
    
    // Remove existing classes
    document.body.classList.remove('do-not-add-cart');
    document.body.classList.remove('regular-product-on-cart');
    document.body.classList.remove('special-product-on-cart');
    document.body.classList.remove('has-cake-products');
    document.body.classList.remove('has-custom-bar-wrappers');

    const cartItems = await this.getCurrentCartItems();
    // @ts-ignore - ProductType is defined in liquid template
    const currentProductType = typeof ProductType !== 'undefined' ? ProductType : null;
    
    // If cart is empty, no conflicts possible
    if (cartItems.length === 0) {
      console.log('[CartValidation] Cart is empty - no conflicts');
      return;
    }

    // Get product types in cart
    const cartProductTypes = cartItems.map((/** @type {any} */ item) => item.product_type);
    const uniqueCartTypes = [...new Set(cartProductTypes)];
    
    // Check if cart has regular products (not Custom Cake or Customized)
    const hasRegularProducts = uniqueCartTypes.some(type => 
      type !== this.customBarWrapperType && type !== this.cakeType && type !== this.CustomBox
    );

    // Check if cart has special products (Custom Cake or Customized)
    const hasSpecialProducts = uniqueCartTypes.some(type => 
      type === this.customBarWrapperType || type === this.cakeType || type === this.CustomBox
    );

    // Check if cart has cake products
    const hasCakeProducts = uniqueCartTypes.some(type => 
      type === this.cakeType
    );

    // Check if cart has custom bar wrappers
    const hasCustomBarWrappers = uniqueCartTypes.some(type => 
      type === this.customBarWrapperType
    );

    // Add classes based on cart contents
    if (hasRegularProducts) {
      document.body.classList.add('regular-product-on-cart');
    }

    if (hasSpecialProducts) {
      document.body.classList.add('special-product-on-cart');
    }

    if (hasCakeProducts) {
      document.body.classList.add('has-cake-products');
    }

    if (hasCustomBarWrappers) {
      document.body.classList.add('has-custom-bar-wrappers');
    }

    // Check for conflicts
    let hasConflict = false;

    if (currentProductType) {
      // We're on a PDP
      if (currentProductType === this.customBarWrapperType || currentProductType === this.cakeType) {
        // Current product is special - check if cart has other types
        const hasOtherTypes = uniqueCartTypes.some(type => 
          type !== currentProductType
        );
        if (hasOtherTypes) {
          hasConflict = true;
          console.log('[CartValidation] Conflict: Special product but cart has other types');
        }
      } else {
        // Current product is normal - check if cart has special types
        const hasSpecialTypes = uniqueCartTypes.some(type => 
          type === this.customBarWrapperType || type === this.cakeType
        );
        if (hasSpecialTypes) {
          hasConflict = true;
          console.log('[CartValidation] Conflict: Normal product but cart has special types');
        }
      }
    } else {
      // Not on PDP - check if cart has special types (prevents adding anything else)
      const hasSpecialTypes = uniqueCartTypes.some(type => 
        type === this.customBarWrapperType || type === this.cakeType
      );
      if (hasSpecialTypes) {
        hasConflict = true;
        console.log('[CartValidation] Conflict: Cart has special types, not on special product page');
      }
    }

    if (hasConflict) {
      document.body.classList.add('do-not-add-cart');
      console.log('[CartValidation] Added class: do-not-add-cart');
    } else {
      console.log('[CartValidation] No conflicts found');
    }
  }
}

// Initialize cart validation
const cartValidation = new CartValidation();

// Export for use in other modules
/** @type {any} */
(window).cartValidation = cartValidation;